/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import {
    useCreateDialogChip,
    useCreateDialogForm,
    useCreateDialogRequest,
    useCreateRoute,
} from 'components/create-dialog/CreateDialogContext';
import { draftIdIn, useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Loader from 'components/Loader';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import { defaultCurrencyOption, useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useCurrentEmployee } from 'pages/employees/useCurrentEmployee';
import { useMemo, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useSelector } from 'store';
import { useAddExpenseMutation, useGetExpenseByIdQuery, useUpdateExpenseMutation } from 'store/api/expenses';
import { useAddInvoiceExpenseMutation } from 'store/api/invoice-details';
import { useGetMattersListQuery } from 'store/api/matters';
import { ExpenseCreateDto, ExpenseForm } from 'types/expenses';
import { InvoiceExpense } from 'types/invoice-details';
import { errorAlert } from 'utils/alert';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { EXPENSE_FORM_CLASSIFIERS, expenseDtoToForm, expenseFormToRequest } from './expenseFormMapping';
import {
    DateAndDescriptionFields,
    EmployeeField,
    MatterField,
    NonBillableField,
    TypeAndAmountFields,
} from './form-sections/ExpenseFields';
import { EXPENSE_FORM_DEFAULTS, useExpenseForm } from './form-sections/useExpenseForm';
import { useConfirmDeleteExpense } from './useConfirmDeleteExpense';

/** Add expense on an invoice: the expense is booked on the invoice's case and created on the invoice. */
type InvoiceCase = {
    /** `null` where the case cannot be read: the field stays empty, and Create refuses it. */
    matter: ExpenseForm['matter'];
    caseNumber: string | null;
    /** Creates the expense on the invoice, which adds it to the case too. */
    create: (dto: ExpenseCreateDto) => Promise<InvoiceExpense>;
};

type Props = {
    initialData?: ExpenseForm;
    refetch?: () => void;
    isDeletable?: boolean;
    isEditable?: boolean;
    /** The case is fixed and Non-billable is not asked: the invoice bills what it holds. */
    invoiceCase?: InvoiceCase;
};

const SECTION = 'expenses.expense';

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    matter: SECTION,
    expenseType: SECTION,
    expenseAmount: SECTION,
    currencyType: SECTION,
    employee: SECTION,
    expenseDate: SECTION,
    description: SECTION,
    isNonBillable: SECTION,
};

const ExpensePageForm = ({
    initialData,
    refetch,
    isDeletable = false,
    isEditable = true,
    invoiceCase,
}: Props): JSX.Element => {
    useFocusedEditMode('expenses');

    const { params, location, inDialog } = useCreateRoute();
    const { id: expenseId } = params;
    const isEdit = !!expenseId;
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    // A matter-context "Add expense" affordance (matter Expenses view / quick-add, dashboard)
    // navigates here with the full case to prefill, so no extra fetch is needed to seed it.
    const presetMatter = (location.state as { presetMatter?: ExpenseForm['matter'] } | null)?.presetMatter ?? null;

    const { data: currentEmployee, isLoading: isLoadingCurrentEmployee } = useCurrentEmployee();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['CURRENCY'] });
    const { code: defaultCurrencyCode, isLoading: isLoadingDefaultCurrency } = useDefaultCurrencyCode();

    // Edit and a draft feed `initialData`; a create seeds the current employee as "Added by", the firm's default
    // currency and the case: the invoice's, or the one a matter context arrived with.
    const computedInitial = useMemo<ExpenseForm | undefined>(() => {
        if (initialData) {
            return initialData;
        }
        if (isLoadingCurrentEmployee || isLoadingClassifiers || isLoadingDefaultCurrency) {
            return undefined;
        }
        return {
            ...EXPENSE_FORM_DEFAULTS,
            employee: currentEmployee
                ? { id: currentEmployee.id, name: `${currentEmployee.firstName} ${currentEmployee.lastName}` }
                : null,
            matter: invoiceCase ? invoiceCase.matter : presetMatter,
            currencyType: defaultCurrencyOption(
                getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']),
                defaultCurrencyCode
            ),
        };
    }, [
        initialData,
        currentEmployee,
        isLoadingCurrentEmployee,
        presetMatter,
        classifiers,
        isLoadingClassifiers,
        defaultCurrencyCode,
        isLoadingDefaultCurrency,
        invoiceCase,
    ]);

    const form = useExpenseForm({ initialData: computedInitial, isEdit });
    useCreateDialogForm(form.methods);
    const draft = useCreateDraft({
        record: 'expense',
        methods: form.methods,
        // An amount left empty is stored as none.
        toPayload: (values: ExpenseForm) => ({
            ...expenseFormToRequest(values),
            expenseAmountOriginalCurrency: values.expenseAmount || null,
        }),
        withoutDraft: !!invoiceCase,
    });
    useCreateDialogChip({ parent: invoiceCase?.caseNumber });
    const createDialog = useCreateDialogRequest();
    const { handleSubmit, control, reset } = form.methods;
    const { errors } = useFormState({ control });
    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const returnTo = useReturnToLocation(expenseId ? `/expense/${expenseId}` : '/expenses');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const [addExpense] = useAddExpenseMutation();
    const [updateExpense] = useUpdateExpenseMutation();
    const confirmDelete = useConfirmDeleteExpense();

    const cancelEditing = () => {
        reset();
        goBack();
    };

    const handleDeleteExpense = () => {
        if (!expenseId || isNaN(Number(expenseId))) {
            return;
        }
        confirmDelete(Number(expenseId));
    };

    const onSubmit = async (formData: ExpenseForm): Promise<void> => {
        try {
            const dto = expenseFormToRequest(formData);
            if (isEdit && expenseId && !isNaN(Number(expenseId))) {
                await updateExpense({ ...dto, id: Number(expenseId) }).unwrap();
                refetch?.();
                leaveForm(`/expense/${expenseId}`);
            } else if (invoiceCase) {
                const { id } = (await invoiceCase.create(dto)).expense;
                // The invoice behind the dialog is re-entered with the new expense's id, as with new timecards.
                if (createDialog) {
                    createDialog.finish(`/expense/${id}`, { pageState: { ...location.state, newExpenseIds: [id] } });
                } else {
                    leaveForm(returnTo);
                }
            } else {
                // A draft is created by completing it: the expense keeps the draft's id.
                const created = draft.draftId === null ? await addExpense(dto).unwrap() : await draft.complete(dto);
                // The dialog stays on the page it was opened over, so it finishes with the new expense instead.
                leaveForm(inDialog ? `/expense/${created.id}` : returnTo);
            }
        } catch (e) {
            // Let the unwrap rejection reach here so the user gets feedback; a swallowed
            // .catch(console.error) would only log and leave them stuck on the form.
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitForm = () => {
        handleSubmit((data) => onSubmit(data))();
    };

    const getFormHeader = () => {
        const savePermissions = isEdit
            ? ['UPDATE.EXPENSES.ALL', 'UPDATE.EXPENSES.MATTER_ACCESS', 'UPDATE.EXPENSES.MY_OWN']
            : ['CREATE.EXPENSES'];
        // The Netvisor integration locks manual expense entry; an existing expense may also be
        // non-editable. The entry buttons already gate on these, but guard the form too so a direct
        // URL can't bypass them (matches the old modal going read-only).
        const saveDisabled = buttonsDisabled || isExpenseEntryDisabled || (isEdit && !isEditable);
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={cancelEditing} />];
        if (!isEdit && draft.button) {
            actions.push(draft.button);
        }
        if (isEdit) {
            actions.push(
                <DeleteButton
                    key="delete"
                    permissions={['DELETE.EXPENSES.ALL', 'DELETE.EXPENSES.MATTER_ACCESS', 'DELETE.EXPENSES.MY_OWN']}
                    onClick={handleDeleteExpense}
                    disabled={buttonsDisabled || !isDeletable || isExpenseEntryDisabled}
                />
            );
        }
        actions.push(
            <SaveButton key="save" permissions={savePermissions} onClick={submitForm} disabled={saveDisabled} />
        );

        return (
            <ActionHeader
                title={isEdit ? t('expenses.edit') : t('expenses.add')}
                actions={actions}
                onBack={cancelEditing}
                draft={draft.detail}
            />
        );
    };

    if (form.isLoading || computedInitial === undefined) {
        return <Loader />;
    }

    return (
        <SectionActionsProvider>
            <HighlightedFieldsProvider>
                <FormWrapper>
                    {getFormHeader()}
                    <DraftSaveMode mode={draft.mode}>
                        <SectionLayout sectionErrors={sectionErrors}>
                            <Section title={SECTION} collapsible>
                                <MatterField form={form} disabled={!!invoiceCase} />
                                <TypeAndAmountFields
                                    form={form}
                                    amountLabel={invoiceCase ? 'expenses.form.net-amount' : undefined}
                                />
                                <EmployeeField form={form} />
                                <DateAndDescriptionFields form={form} />
                                {invoiceCase ? null : <NonBillableField form={form} />}
                            </Section>
                        </SectionLayout>
                    </DraftSaveMode>
                </FormWrapper>
            </HighlightedFieldsProvider>
        </SectionActionsProvider>
    );
};

/** The create form loaded with what a draft holds, read as the expense's edit page reads it. */
const ExpenseDraftForm = ({ draftId }: { draftId: number }) => {
    const [initialData, setInitialData] = useState<ExpenseForm>();
    const { data: expense } = useGetExpenseByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EXPENSE_FORM_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!initialData && expense && !isLoadingClassifiers) {
        setInitialData(expenseDtoToForm(expense, classifiers));
    }

    return initialData ? <ExpensePageForm initialData={initialData} /> : <Loader />;
};

/** Add expense on an invoice's case, which it reads as the invoice's former expense dialog did. */
const InvoiceExpensePageForm = ({ invoiceId, matterId }: { invoiceId: number; matterId: number }) => {
    const [addInvoiceExpense] = useAddInvoiceExpenseMutation();
    const { data, isLoading } = useGetMattersListQuery({ pageSize: 1, page: 0, filters: `id==${matterId}` });
    const found = data?.content.at(0);

    const invoiceCase = useMemo<InvoiceCase>(
        () => ({
            matter: found
                ? {
                      id: found.id,
                      name: found.name,
                      fullName: found.fullName,
                      isNonBillable: found.isNonBillable,
                      isConfidential: found.isConfidential,
                      visibility: found.visibility,
                  }
                : null,
            caseNumber: found?.caseNumber ?? null,
            create: (dto) => addInvoiceExpense({ invoiceId, matterId, dto }).unwrap(),
        }),
        [found, addInvoiceExpense, invoiceId, matterId]
    );

    return isLoading ? <Loader /> : <ExpensePageForm invoiceCase={invoiceCase} />;
};

/**
 * The expense form; the create URL with `?draft=<id>` edits that draft instead of a blank expense, and the one under
 * an invoice's case adds the expense to that invoice.
 */
const ExpensePageFormOrDraft = (props: Props) => {
    const { location, params } = useCreateRoute();
    const draftId = draftIdIn(location.search);
    if (props.initialData === undefined && params.invoiceId && params.matterId) {
        return <InvoiceExpensePageForm invoiceId={Number(params.invoiceId)} matterId={Number(params.matterId)} />;
    }
    return props.initialData === undefined && draftId !== null ? (
        <ExpenseDraftForm key={draftId} draftId={draftId} />
    ) : (
        <ExpensePageForm {...props} />
    );
};

export default ExpensePageFormOrDraft;
